import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Container,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useNavigate } from "react-router-dom";
import { useGetBanquetBookingsMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useStyles } from "../../../theme/appStyles";

// ── Types ──────────────────────────────────────────────────────────────────────
interface BookingRow {
  id: number;
  bookingRef: string;
  banquetName: string;
  eventType: string;
  eventDate: string;
  timeSlot: string;
  status: string;
  guestCount: number;
  grandTotal: number;
  halls: { hallKey: string; hallLabel: string }[];
  primaryCustomer: { firstName?: string; lastName?: string } | null;
}

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmtCAD = (v: number) =>
  v.toLocaleString("en-CA", { style: "currency", currency: "CAD" });

const fmtDate = (v: string) =>
  new Date(v + "T12:00:00").toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const TIME_LABELS: Record<string, string> = {
  AM: "AM",
  PM: "PM",
  FULL_DAY: "Full Day",
};

// ── Status colours ─────────────────────────────────────────────────────────────
const STATUS_COLOR: Record<
  string,
  "warning" | "success" | "info" | "primary" | "error" | "default"
> = {
  quote: "warning",
  order: "success",
  invoice: "info",
  complete: "primary",
  cancel: "error",
};

// ── Hall config ────────────────────────────────────────────────────────────────
const HALLS = [
  { key: "Nova",    label: "Nova",    color: "#1565c0" },
  { key: "Titan",   label: "Titan",   color: "#2e7d32" },
  { key: "Lunar",   label: "Lunar",   color: "#e65100" },
  { key: "Eclipse", label: "Eclipse", color: "#6a1b9a" },
];

// ── Component ──────────────────────────────────────────────────────────────────
const BanquetDashboard = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const classes  = useStyles();
  const navigate = useNavigate();
  const { showErrorMessage } = useNotifier();

  const [rows,       setRows]       = useState<BookingRow[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  const [getBookings] = useGetBanquetBookingsMutation();

  // ── Date anchors ──────────────────────────────────────────────────────────
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const in14 = new Date(today);
  in14.setDate(in14.getDate() + 14);
  const todayStr = today.toISOString().split("T")[0];

  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const thisMonthEnd   = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setIsFetching(true);
      try {
        const res = (await getBookings({} as any).unwrap()) as any;
        if (res.status && Array.isArray(res.data)) {
          setRows(res.data as BookingRow[]);
        }
      } catch (err: any) {
        showErrorMessage(
          err?.data?.message ?? err?.message ?? "Failed to load bookings"
        );
      } finally {
        setIsFetching(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Derived stats ─────────────────────────────────────────────────────────
  const confirmedStatuses = new Set(["order", "invoice", "complete"]);

  const confirmedThisMonth = rows.filter((r) => {
    if (!confirmedStatuses.has(r.status)) return false;
    const d = new Date(r.eventDate + "T12:00:00");
    return d >= thisMonthStart && d <= thisMonthEnd;
  }).length;

  const pendingQuotes = rows.filter(
    (r) => r.status === "quote"
  ).length;

  const estRevenue = rows.reduce((sum, r) => {
    if (!confirmedStatuses.has(r.status)) return sum;
    const d = new Date(r.eventDate + "T12:00:00");
    if (d < thisMonthStart || d > thisMonthEnd) return sum;
    return sum + (Number(r.grandTotal) || 0);
  }, 0);

  const todayBookings = rows.filter(
    (r) => r.eventDate === todayStr && r.status !== "cancel"
  ).length;

  // ── Upcoming (next 14 days, not cancelled) ────────────────────────────────
  const upcoming = rows
    .filter((r) => {
      if (r.status === "cancel") return false;
      const d = new Date(r.eventDate + "T12:00:00");
      d.setHours(0, 0, 0, 0);
      return d >= today && d <= in14;
    })
    .sort((a, b) => a.eventDate.localeCompare(b.eventDate));

  // ── Today's hall status ───────────────────────────────────────────────────
  const todayActive = rows.filter(
    (r) => r.eventDate === todayStr && r.status !== "cancel"
  );

  const getHallSlotBooking = (hallKey: string, slot: "AM" | "PM") => {
    return todayActive.find((r) => {
      const hasHall = r.halls?.some(
        (h) => (h.hallKey || "").toLowerCase() === hallKey.toLowerCase()
      );
      if (!hasHall) return false;
      if (r.timeSlot === "FULL_DAY") return true;
      return r.timeSlot === slot;
    });
  };

  // ── Stat cards data ───────────────────────────────────────────────────────
  const stats = [
    {
      label:    "Confirmed orders",
      value:    confirmedThisMonth.toString(),
      subtitle: "This month",
    },
    {
      label:    "Pending quotes",
      value:    pendingQuotes.toString(),
      subtitle: "Awaiting confirmation",
    },
    {
      label:    "Est. revenue",
      value:    fmtCAD(estRevenue),
      subtitle: "Orders & invoices this month",
    },
    {
      label:    "Today's bookings",
      value:    todayBookings.toString(),
      subtitle: "Happening today",
    },
  ];

  const cellSx = { py: "8px", px: "12px", fontSize: 13 };

  return (
    <Container maxWidth="xl" disableGutters={isMobile} sx={{ px: { xs: 0, sm: 1, md: 2 } }}>
      <Box sx={{ pb: { xs: 10, sm: 12 } }}>

        {/* ── Page heading ── */}
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 900,
            display: "flex",
            alignItems: "center",
            color: "text.primary",
            fontSize: { xs: 16, sm: 18, md: 20 },
            mb: 2,
          }}
        >
          <EventIcon
            sx={{
              mr: 1,
              width:  { xs: 22, sm: 26, md: 28 },
              height: { xs: 22, sm: 26, md: 28 },
            }}
          />
          Banquet Dashboard
        </Typography>

        {isFetching && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

        {/* ── Stats row ── */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {stats.map((s) => (
            <Grid item xs={12} sm={6} md={3} key={s.label}>
              <Paper
                variant="outlined"
                sx={{ p: 2, height: "100%" }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  {s.label}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, lineHeight: 1.2, mb: 0.5 }}
                >
                  {isFetching ? (
                    <CircularProgress size={22} />
                  ) : (
                    s.value
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {s.subtitle}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* ── Upcoming bookings table ── */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, fontSize: 15 }}>
          Upcoming bookings — Next 14 days
        </Typography>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflowX: "auto",
            mb: 3,
          }}
        >
          <Table size="small" sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: "action.hover",
                  "& th": {
                    fontWeight: 700,
                    fontSize: 13,
                    whiteSpace: "nowrap",
                    py: "8px",
                    px: "12px",
                  },
                }}
              >
                <TableCell>Ref</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Event Type</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Hall(s)</TableCell>
                <TableCell>Time Slot</TableCell>
                <TableCell align="right">Guests</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center" sx={{ width: 40 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {upcoming.length === 0 && !isFetching && (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    align="center"
                    sx={{ py: 4, color: "text.secondary", fontSize: 13 }}
                  >
                    No upcoming bookings in the next 14 days.
                  </TableCell>
                </TableRow>
              )}
              {upcoming.map((row) => {
                const hallLabel =
                  row.halls?.map((h) => h.hallLabel || h.hallKey).join(", ") || "—";
                const custName = row.primaryCustomer
                  ? [row.primaryCustomer.firstName, row.primaryCustomer.lastName]
                      .filter(Boolean)
                      .join(" ") || "—"
                  : "—";

                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: "pointer", "&:last-child td": { border: 0 } }}
                    onClick={() => navigate(`/banquet/booking/${row.id}`)}
                  >
                    <TableCell sx={cellSx}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13 }}>
                        {row.bookingRef}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>
                      {row.eventDate ? fmtDate(row.eventDate) : "—"}
                    </TableCell>
                    <TableCell sx={cellSx}>{row.eventType || "—"}</TableCell>
                    <TableCell sx={cellSx}>{custName}</TableCell>
                    <TableCell sx={cellSx}>{hallLabel}</TableCell>
                    <TableCell sx={cellSx}>
                      {TIME_LABELS[row.timeSlot] ?? row.timeSlot ?? "—"}
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      {row.guestCount ?? "—"}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Chip
                        label={
                          row.status
                            ? row.status.charAt(0).toUpperCase() + row.status.slice(1)
                            : "—"
                        }
                        size="small"
                        color={STATUS_COLOR[row.status] ?? "default"}
                        sx={{ fontSize: 11, height: 22 }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={cellSx}>
                      <ArrowForwardIosIcon sx={{ fontSize: 13, color: "text.disabled" }} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Today's hall status ── */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, fontSize: 15 }}>
          Today's hall status
        </Typography>

        <Grid container spacing={2}>
          {HALLS.map((hall) => {
            const amBooking = getHallSlotBooking(hall.key, "AM");
            const pmBooking = getHallSlotBooking(hall.key, "PM");

            return (
              <Grid item xs={12} sm={6} md={3} key={hall.key}>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: "center" }}>
                  {/* Hall name */}
                  <Chip
                    label={hall.label}
                    size="small"
                    sx={{
                      bgcolor: hall.color,
                      color: "#fff",
                      fontWeight: 700,
                      mb: 1.5,
                      fontSize: 12,
                    }}
                  />

                  {/* AM slot */}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", minWidth: 28 }}>
                      AM
                    </Typography>
                    {amBooking ? (
                      <Chip
                        label={
                          (amBooking.banquetName || amBooking.bookingRef || "Booked").length > 18
                            ? (amBooking.banquetName || amBooking.bookingRef || "Booked").slice(0, 16) + "…"
                            : amBooking.banquetName || amBooking.bookingRef || "Booked"
                        }
                        size="small"
                        color="warning"
                        sx={{ fontSize: 11, height: 22, flex: 1, ml: 0.5, justifyContent: "center" }}
                      />
                    ) : (
                      <Chip
                        label="Free"
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ fontSize: 11, height: 22, flex: 1, ml: 0.5 }}
                      />
                    )}
                  </Box>

                  {/* PM slot */}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", minWidth: 28 }}>
                      PM
                    </Typography>
                    {pmBooking ? (
                      <Chip
                        label={
                          (pmBooking.banquetName || pmBooking.bookingRef || "Booked").length > 18
                            ? (pmBooking.banquetName || pmBooking.bookingRef || "Booked").slice(0, 16) + "…"
                            : pmBooking.banquetName || pmBooking.bookingRef || "Booked"
                        }
                        size="small"
                        color="warning"
                        sx={{ fontSize: 11, height: 22, flex: 1, ml: 0.5, justifyContent: "center" }}
                      />
                    ) : (
                      <Chip
                        label="Free"
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ fontSize: 11, height: 22, flex: 1, ml: 0.5 }}
                      />
                    )}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

      </Box>
    </Container>
  );
};

export default BanquetDashboard;
